import { useState } from "react";
import { useData } from "../context/DataContext";
import {
  Plus,
  Trash2,
  ChevronDown,
  ChevronRight,
  Edit,
} from "lucide-react";
import { InlineSelect } from "./InlineSelect";
import { Modal, Button, Input, Select, EmptyState } from "./ui";
import { useExpandableRows, useDeleteConfirmation, useFormReset } from "../utils/hooks";
import { ZUTEILUNG_TYPES } from "../utils/constants";

const Klassen = () => {
  const {
    data,
    addKlasse,
    deleteKlasse,
    updateKlasse,
    addZuteilung,
    deleteZuteilung,
    updateZuteilung,
  } = useData();

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const { toggleRow, isExpanded } = useExpandableRows();
  const { formData, setFormData, resetForm } = useFormReset({
    name: "",
    stufe: "1",
    profil: "US",
    bestand: "20",
    semester: "HS" as "HS" | "FS",
    schuljahr: "2025",
    klassenlehrerId: "",
  });

  const confirmDelete = useDeleteConfirmation("Klasse", deleteKlasse);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const klasseData = {
      name: formData.name,
      stufe: parseInt(formData.stufe),
      profil: formData.profil,
      bestand: parseInt(formData.bestand),
      semester: formData.semester,
      schuljahr: parseInt(formData.schuljahr),
      klassenlehrerId: formData.klassenlehrerId || null,
    };

    if (editingId) {
      updateKlasse(editingId, klasseData);
    } else {
      addKlasse(klasseData);
    }
    resetForm();
    setEditingId(null);
    setShowModal(false);
  };

  const handleEdit = (klasseId: string) => {
    const klasse = data.klassen.find((k) => k.id === klasseId);
    if (klasse) {
      setFormData({
        name: klasse.name,
        stufe: String(klasse.stufe),
        profil: klasse.profil,
        bestand: String(klasse.bestand),
        semester: klasse.semester,
        schuljahr: String(klasse.schuljahr),
        klassenlehrerId: klasse.klassenlehrerId || "",
      });
      setEditingId(klasseId);
      setShowModal(true);
    }
  };

  const handleNewKlasse = () => {
    resetForm();
    setEditingId(null);
    setShowModal(true);
  };

  const handleAddZuteilung = (klasseId: string) => {
    if (data.lehrpersonen.length === 0 || data.faecher.length === 0) {
      alert("Bitte erst Lehrpersonen und Fächer anlegen!");
      return;
    }
    addZuteilung({
      lpId: data.lehrpersonen[0].id,
      klasseId,
      fachId: data.faecher[0].id,
      typ: "Standard",
      lektionen: 2,
      hatHk: false,
      bemerkungen: "",
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-3">
        <Button
          onClick={handleNewKlasse}
          variant="primary"
          icon={<Plus className="w-4 h-4" />}
        >
          Neue Klasse
        </Button>
      </div>

      {data.klassen.length === 0 ? (
        <EmptyState
          icon="🎓"
          title="Keine Klassen vorhanden."
          description='Klicken Sie auf "Neue Klasse" um zu beginnen'
        />
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8">
                    {/* Expand icon */}
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Klassenname
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stufe
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Profil
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bestand
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Semester
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Schuljahr
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Klassenlehrer
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.klassen.map((kl) => {
                  const isRowExpanded = isExpanded(kl.id);
                  const klasseZuteilungen = data.zuteilungen.filter(
                    (zt) => zt.klasseId === kl.id
                  );

                  return (
                    <>
                      <tr
                        key={kl.id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-4 py-2 whitespace-nowrap">
                          <button
                            onClick={() => toggleRow(kl.id)}
                            className="p-1 hover:bg-gray-200 rounded transition-colors"
                          >
                            {isRowExpanded ? (
                              <ChevronDown className="w-4 h-4 text-gray-600" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-600" />
                            )}
                          </button>
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          <div className="font-medium text-gray-900">
                            {kl.name}
                          </div>
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                          {kl.stufe}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {kl.profil}
                          </span>
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                          {kl.bestand}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                          {kl.semester}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                          {kl.schuljahr}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          <InlineSelect
                            value={kl.klassenlehrerId}
                            options={data.lehrpersonen.map((lp) => ({
                              id: lp.id,
                              label: lp.name,
                            }))}
                            onChange={(value) =>
                              updateKlasse(kl.id, { klassenlehrerId: value })
                            }
                            placeholder="-"
                            allowClear
                            displayClassName="text-sm text-gray-500"
                          />
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-center">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => handleEdit(kl.id)}
                              className="inline-flex items-center p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Bearbeiten"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => confirmDelete(kl.id, kl.name)}
                              className="inline-flex items-center p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Löschen"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* Expanded Row for Zuteilungen */}
                      {isRowExpanded && (
                        <tr>
                          <td
                            colSpan={9}
                            className="px-4 py-3 bg-gray-50 border-t border-gray-200"
                          >
                            <div className="space-y-4">
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="text-sm font-semibold text-gray-700">
                                  Zuteilungen für {kl.name}
                                </h4>
                                <button
                                  onClick={() => handleAddZuteilung(kl.id)}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
                                >
                                  <Plus className="w-4 h-4" />
                                  Neue Zuteilung
                                </button>
                              </div>

                              {klasseZuteilungen.length === 0 ? (
                                <p className="text-sm text-gray-500 text-center py-4">
                                  Keine Zuteilungen vorhanden
                                </p>
                              ) : (
                                <div className="overflow-x-auto">
                                  <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-100">
                                      <tr>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                          Lehrperson
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                          Fach
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                          Typ
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                          Lektionen
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                          HK
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                          Bemerkungen
                                        </th>
                                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                                          Aktionen
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                      {klasseZuteilungen.map((zt) => (
                                        <tr
                                          key={zt.id}
                                          className="hover:bg-gray-50"
                                        >
                                          <td className="px-4 py-2 whitespace-nowrap">
                                            <InlineSelect
                                              value={zt.lpId}
                                              options={data.lehrpersonen.map(
                                                (lp) => ({
                                                  id: lp.id,
                                                  label: lp.name,
                                                })
                                              )}
                                              onChange={(value) =>
                                                updateZuteilung(zt.id, {
                                                  lpId: value,
                                                })
                                              }
                                              placeholder="-"
                                              allowClear
                                              displayClassName="text-sm"
                                            />
                                          </td>
                                          <td className="px-4 py-2 whitespace-nowrap">
                                            <InlineSelect
                                              value={zt.fachId}
                                              options={data.faecher.map(
                                                (f) => ({
                                                  id: f.id,
                                                  label: f.name,
                                                })
                                              )}
                                              onChange={(value) =>
                                                updateZuteilung(zt.id, {
                                                  fachId: value || "",
                                                })
                                              }
                                              placeholder="Wählen..."
                                              displayClassName="text-sm"
                                            />
                                          </td>
                                          <td className="px-4 py-2 whitespace-nowrap">
                                            <InlineSelect
                                              value={zt.typ}
                                              options={ZUTEILUNG_TYPES}
                                              onChange={(value) =>
                                                updateZuteilung(zt.id, {
                                                  typ: value as any,
                                                })
                                              }
                                              placeholder="Wählen..."
                                              displayClassName="text-sm"
                                            />
                                          </td>
                                          <td className="px-4 py-2 whitespace-nowrap">
                                            <input
                                              type="number"
                                              value={zt.lektionen}
                                              onChange={(e) =>
                                                updateZuteilung(zt.id, {
                                                  lektionen: parseFloat(
                                                    e.target.value
                                                  ),
                                                })
                                              }
                                              step="0.5"
                                              min="0"
                                              className="w-20 px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                                            />
                                          </td>
                                          <td className="px-4 py-2 whitespace-nowrap text-center">
                                            <input
                                              type="checkbox"
                                              checked={zt.hatHk}
                                              onChange={(e) =>
                                                updateZuteilung(zt.id, {
                                                  hatHk: e.target.checked,
                                                })
                                              }
                                              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                            />
                                          </td>
                                          <td className="px-4 py-2">
                                            <input
                                              type="text"
                                              value={zt.bemerkungen}
                                              onChange={(e) =>
                                                updateZuteilung(zt.id, {
                                                  bemerkungen: e.target.value,
                                                })
                                              }
                                              placeholder="Bemerkungen..."
                                              className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                                            />
                                          </td>
                                          <td className="px-4 py-2 whitespace-nowrap text-center">
                                            <button
                                              onClick={() => {
                                                if (
                                                  window.confirm(
                                                    "Zuteilung wirklich löschen?"
                                                  )
                                                ) {
                                                  deleteZuteilung(zt.id);
                                                }
                                              }}
                                              className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                                              title="Löschen"
                                            >
                                              <Trash2 className="w-4 h-4" />
                                            </button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingId ? "Klasse bearbeiten" : "Neue Klasse"}
      >
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <Input
            label="Klassenname *"
            type="text"
            required
            value={formData.name}
            onChange={(e) =>
              setFormData({ ...formData, name: e.target.value })
            }
            placeholder="z.B. 1a, 3c"
          />
          <Select
            label="Stufe *"
            value={formData.stufe}
            onChange={(e) =>
              setFormData({ ...formData, stufe: e.target.value })
            }
          >
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <option key={i} value={i}>
                {i}
              </option>
            ))}
          </Select>
          <Input
            label="Profil *"
            type="text"
            required
            value={formData.profil}
            onChange={(e) =>
              setFormData({ ...formData, profil: e.target.value })
            }
            placeholder="US, L/E, I/E, Sp/E, etc."
          />
          <Input
            label="Schüleranzahl *"
            type="number"
            required
            min="1"
            value={formData.bestand}
            onChange={(e) =>
              setFormData({ ...formData, bestand: e.target.value })
            }
          />
          <Select
            label="Semester *"
            value={formData.semester}
            onChange={(e) =>
              setFormData({
                ...formData,
                semester: e.target.value as "HS" | "FS",
              })
            }
          >
            <option value="HS">HS</option>
            <option value="FS">FS</option>
          </Select>
          <Input
            label="Schuljahr *"
            type="number"
            required
            value={formData.schuljahr}
            onChange={(e) =>
              setFormData({ ...formData, schuljahr: e.target.value })
            }
          />
          <Select
            label="Klassenlehrer (optional)"
            value={formData.klassenlehrerId}
            onChange={(e) =>
              setFormData({
                ...formData,
                klassenlehrerId: e.target.value,
              })
            }
          >
            <option value="">- Keiner -</option>
            {data.lehrpersonen.map((lp) => (
              <option key={lp.id} value={lp.id}>
                {lp.name}
              </option>
            ))}
          </Select>
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <Button type="submit" variant="primary" className="flex-1">
              Speichern
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowModal(false)}
              className="flex-1"
            >
              Abbrechen
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Klassen;
